import React from 'react';

import gfm from 'remark-gfm';

import Data from 'data';
  
import { getIconPath, getStoredValue, getTypeIconsPath } from 'common';
import { D3TooltipBuff, D3TooltipFollowerSkill, D3TooltipGem, D3TooltipItem, D3TooltipSet, D3TooltipSkill } from 'tooltips';

const bnetPrefix = ({
  enUS: "https://eu.diablo3.com/en-us/",
  ptBR: "https://eu.diablo3.com/pt-br/",
  frFR: "https://eu.diablo3.com/fr-fr/",
  deDE: "https://eu.diablo3.com/de-de/",
  esES: "https://eu.diablo3.com/es-es/",
  itIT: "https://eu.diablo3.com/it-it/",
  ruRU: "https://eu.diablo3.com/ru-ru/",
  koKR: "https://eu.diablo3.com/ko-kr/",
  zhCN: "https://eu.diablo3.com/en-us/",
  zhTW: "https://eu.diablo3.com/zh-tw/",
  plPL: "https://eu.diablo3.com/pl-pl/",
})[getStoredValue("locale", "enUS")];

const bnetSlug = name => name.replace(/[^\w ]/g, "").replace(/ /g,"-").toLowerCase();
const bnetItemUrl = item => `${bnetPrefix}/item/${bnetSlug(item.defaultName)}-${item.id}`;
const bnetGemUrl = (quality, item) => `${bnetPrefix}/artisan/jeweler/recipe/${bnetSlug(item.defaultNames[quality])}`;
const bnetSkillUrl = (charClass, info) => `${bnetPrefix}/class/${bnetSlug(Data.classes[charClass].defaultName)}/active/${bnetSlug(info.defaultName)}`;
const bnetFollowerSkillUrl = info => `${bnetPrefix}/follower/${bnetSlug(Data.classes[info.class].defaultName)}/skill/${bnetSlug(info.defaultName)}`;
const bnetPassiveUrl = (charClass, info) => `${bnetPrefix}/class/${bnetSlug(Data.classes[charClass].defaultName)}/passive/${bnetSlug(info.defaultName)}`;

const Link = ({ children, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>;

function itemIcon(type, id, index=0) {
  const icon = Data.itemIcons[Data.itemById[id]?.itemid || id];
  if (!icon) return null;
  return (
    <span className="d3md-icon-wrap">
      <span className="d3md-icon" style={{backgroundImage: `url(${getTypeIconsPath(type)})`, backgroundPosition: `${-index}em ${-icon[0]}em`}}/>
    </span>
  );
}
function itemIconSpecial(icon) {
  if (!icon) return null;
  return (
    <span className="d3md-icon-wrap">
      <span className="d3md-icon-special" style={{backgroundImage: `url(${getIconPath(icon)})`}}/>
    </span>
  );
}

function ItemLink({ id, children }) {
  if (Data.itemById[id]) {
    const item = Data.itemById[id];
    return (
      <span className={`d3md-item d3p-quality-${item.quality}`}>
        {itemIcon(item.type, item.realid || item.id)}
        {itemIconSpecial(item.icon)}
        <D3TooltipItem data={item.id}>
          <Link href={bnetItemUrl(item)}>
            {children}
          </Link>
        </D3TooltipItem>
      </span>
    );
  } else if (Data.gemById[id]) {
    const gem = Data.gemById[id];
    if (Data.legendaryGems[gem[0]]) {
      const item = Data.legendaryGems[gem[0]];
      return (
        <span className="d3md-item d3p-quality-legendary">
          {itemIcon("gemleg", item.id)}
          <D3TooltipGem id={gem[0]}>
            <Link href={bnetItemUrl(item)}>
              {children}
            </Link>
          </D3TooltipGem>
        </span>
      );
    } else if (Data.gemQualities[gem[0]] && Data.gemColors[gem[1]]) {
      const item = Data.gemColors[gem[1]];
      const icon = Data.itemIcons[item.id];
      return (
        <span className="d3md-item d3p-quality-magic">
          {!!icon && <span className="d3md-icon-gem" style={{backgroundImage: `url(${getTypeIconsPath("gems")})`, backgroundPosition: `${-gem[0]}em ${-icon[0]}em`}}/>}
          <D3TooltipGem id={gem}>
            <Link href={bnetGemUrl(gem[0], item)}>
              {children}
            </Link>
          </D3TooltipGem>
        </span>
      );
    }
  }
  return children;
}

function SetLink({ id, children }) {
  const set = Data.itemSets[id];
  if (!set) return children;
  return (
    <D3TooltipSet id={id}>
      <span className="d3md-set">
        {children}
      </span>
    </D3TooltipSet>
  );
}

function SkillLink({ type, charClass, skill, rune, children }) {
  const info = Data.skills[charClass]?.[skill] || Data.extraskills[charClass]?.[skill];
  if (!info) return children;
  if (type !== "skill" && !info.runes?.[rune]) return children;

  const element = Data.skilltips[charClass]?.[skill].elements[rune || "x"] || "phy";

  let skillText = children;
  let runeText = null;
  let separator = null;
  if (type === "rune") {
    skillText = null;
    runeText = children;
  } else if (type === "skill-rune") {
    skillText = info.name;
    runeText = info.runes[rune];
    separator = " ";
    if (Array.isArray(children) && children.length === 1) {
      const text = children[0].props?.children;
      if (typeof text === "string") {
        const tm = text.match(new RegExp(skillText + "(.*)" + runeText));
        if (tm) separator = tm[1];
      }
    }
  }

  return (
    <span className="d3md-skill">
      {!!skillText && <span className={`d3p-skill-icon d3p-class-${charClass}`} style={{backgroundPosition: `${-info.col}em ${-2 * info.row}em`}}/>}
      {!!skillText && (
        <D3TooltipSkill charClass={charClass} skill={skill} rune={rune}>
          <Link href={bnetSkillUrl(charClass, info)}>{skillText}</Link>
        </D3TooltipSkill>
      )}
      {separator}
      {!!runeText && <span className={`d3p-rune d3p-rune-${rune}`}/>}
      {!!runeText && (
        <D3TooltipSkill charClass={charClass} skill={skill} rune={rune} onlyRune>
          <span className={`d3md-rune d3md-rune-${element}`}>{runeText}</span>
        </D3TooltipSkill>
      )}
    </span>
  );
}

function PassiveLink({ charClass, passive, children }) {
  const info = Data.passives[charClass]?.[passive];
  if (!info) return children;
  return (
    <span className="d3md-passive">
      <span className={`d3p-passive-icon d3p-class-${charClass}`} style={{backgroundPosition: `${-info.index}em 0`}}/>
      <D3TooltipSkill charClass={charClass} skill={passive}>
        <Link href={bnetPassiveUrl(charClass, info)}>
          {children}
        </Link>
      </D3TooltipSkill>
    </span>
  );
}

function FollowerLink({ skill, children }) {
  const info = Data.followerSkills[skill];
  if (!info) return children;
  return (
    <span className="d3md-follower">
      <span className="d3md-icon" style={{backgroundImage: `url(${getIconPath(info.icon)})`}}/>
      <D3TooltipFollowerSkill id={skill}>
        <Link href={bnetFollowerSkillUrl(info)}>
          {children}
        </Link>
      </D3TooltipFollowerSkill>
    </span>
  );
}

function BuffLink({ id, children }) {
  const info = Data.shrineBuffs[id];
  if (!info) return children;
  return (
    <span className="d3md-buff">
      <span className="d3md-icon" style={{backgroundImage: `url(${getIconPath(info.icon)})`}}/>
      <D3TooltipBuff id={id}>
        <span className="d3md-buff-name">
          {children}
        </span>
      </D3TooltipBuff>
    </span>
  );
}

function LinkRenderer({ href, children }) {
  const parts = href.split('.');
  switch (parts[0]) {
  case 'item': return <ItemLink id={parts[1]}>{children}</ItemLink>;
  case 'set': return <SetLink id={parts[1]}>{children}</SetLink>;
  case 'skill':
  case 'rune':
  case 'skill-rune': return <SkillLink type={parts[0]} charClass={parts[1]} skill={parts[2]} rune={parts[3]}>{children}</SkillLink>;
  case 'passive': return <PassiveLink charClass={parts[1]} passive={parts[2]}>{children}</PassiveLink>;
  case 'follower': return <FollowerLink skill={parts[1]}>{children}</FollowerLink>;
  case 'buff': return <BuffLink id={parts[1]}>{children}</BuffLink>;
  default: return <Link className="d3md-link" href={href}>{children}</Link>;
  }
}

function youtubeId(href) {
  try {
    const url = new URL(href);
    if (url.hostname.match(/^(?:.*\.)?youtube\.com$/i) && url.pathname === "/watch") {
      return url.searchParams.get("v");
    } else if (url.hostname.match(/^youtu\.be$/i)) {
      return url.pathname.substring(1);
    }
  } catch (e) {
    return null;
  }
}

function YoutubePlugin() {
  return function(tree) {
    for (const child of tree.children) {
      if (child.type === "paragraph" && child.children.length === 1 && child.children[0].type === "link") {
        const link = child.children[0];
        const id = youtubeId(link.url);
        if (id && id.match(/^[\w_-]+$/)) {
          child.type = "embed";
          child.children.length = 0;
          child.id = id;
        }
      }
    }  
    return tree;
  }
}

function EmbedRenderer({ id }) {
  return (
    <div className="d3md-embed">
      <iframe width="100%" height="100%" frameBorder="0" src={`https://www.youtube.com/embed/${id}`} title="YouTube"/>
    </div>
  );
}

export const plugins = [gfm, YoutubePlugin];
export const renderers = {link: LinkRenderer, embed: EmbedRenderer};
